import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["value", "required", "disabled"]
const _hoisted_6 = { class: "config-data-section" }
const _hoisted_7 = { class: "config-data-table" }
const _hoisted_8 = {
  key: 0,
  class: "config-data-empty"
}
const _hoisted_9 = ["value", "disabled", "onTextInputChange"]
const _hoisted_10 = { class: "value-cell" }
const _hoisted_11 = ["value", "disabled", "onCodeInputChange"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 2,
  class: "config-data-actions"
}
const _hoisted_15 = { slot: "footer" }
const _hoisted_16 = ["disabled"]

import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';

import { EPINIO_TYPES } from '../../types';
import { epinioExceptionToErrorsArray } from '../../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import Banner from '@components/Banner/Banner.vue';
import isEqual from 'lodash/isEqual';
import ResourceDropdown from '../application/ResourceDropdown.vue';
import { useNamespaces } from '../../utils/namespaces';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConfigurationModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

const showModal = ref(false);
const modalMode = ref<'view' | 'edit' | 'create'>('view');
const configModel = ref<any>(null);

const formNamespace = ref('');
const formName = ref('');
const initialBoundApps = ref<string[]>([]);
const selectedApps = ref<string[]>([]);
const configData = ref<Array<{ key: string; value: string }>>([]);
const initialConfigDataSnapshot = ref('');
const saving = ref(false);
const errors = ref<string[]>([]);
const showDiscardConfirm = ref(false);

// Hidden file inputs, both rendered outside the modal to avoid focus-return side effects
const bulkFileInput = ref<HTMLInputElement | null>(null);
const rowFileInput = ref<HTMLInputElement | null>(null);
const currentUploadRow = ref<number | null>(null);
const fileDialogActive = ref(false);

const isView = computed(() => modalMode.value === 'view');
const isEdit = computed(() => modalMode.value === 'edit');
const isCreate = computed(() => modalMode.value === 'create');
const isEditing = computed(() => isEdit.value || isCreate.value);

const isLoadingApplications = ref(false);
const cachedApplications = ref<any[]>([]);
const fetchedApplications = ref<any[]>([]);

const {
  options:    namespaceOpts,
  isLoading:  isLoadingNamespaces,
  firstName:  firstNamespace,
  fetchAll:   fetchNamespaces,
  search:     searchNamespaces,
} = useNamespaces(store, {
  scopeToActiveFilter: true
});

const nsAppOptions = computed(() => {
  if (!formNamespace.value) return [];

  return fetchedApplications.value
    .map((a: any) => ({ label: a.meta.name, value: a.meta.name }));
});

const isDirty = computed(() => {
  if (isCreate.value) {
    return !!(formName.value || selectedApps.value.length || configData.value.some(r => r.key || r.value));
  }
  if (!isEdit.value || !configModel.value) return false;

  const appsChanged = !isEqual([...selectedApps.value].sort(), [...initialBoundApps.value].sort());
  const dataChanged = JSON.stringify(configData.value) !== initialConfigDataSnapshot.value;

  return appsChanged || dataChanged;
});

const configDataValid = computed(() =>
  configData.value.every(row => row.key.trim() !== '' && row.value.trim() !== '')
);

const validationPassed = computed(() => {
  if (!configDataValid.value) return false;

  if (isCreate.value) {
    if (!formNamespace.value || !formName.value) return false;

    const nameErrors = validateKubernetesName(formName.value, '', store.getters, undefined, []);
    const nsErrors = validateKubernetesName(formNamespace.value, '', store.getters, undefined, []);

    return nameErrors.length === 0 && nsErrors.length === 0;
  }

  return isEdit.value && isDirty.value;
});

const modalTitle = computed(() => isCreate.value ? 'Advanced Configurations' : (formName.value || 'Advanced Configurations'));

const modalSubtitle = computed(() => {
  if (isCreate.value) return 'Create New';

  return formNamespace.value;
});

function detailsToRows(details: Record<string, string>): Array<{ key: string; value: string }> {
  return Object.entries(details || {}).map(([key, value]) => ({ key, value }));
}

function rowsToDetails(rows: Array<{ key: string; value: string }>): Record<string, string> {
  const obj: Record<string, string> = {};

  rows.forEach(({ key, value }) => {
    if (key) obj[key] = value;
  });

  return obj;
}

async function openCreate() {
  errors.value = [];
  modalMode.value = 'create';
  configModel.value = null;
  formNamespace.value = '';
  formName.value = '';
  selectedApps.value = [];
  initialBoundApps.value = [];
  configData.value = [{ key: '', value: '' }];
  initialConfigDataSnapshot.value = '';

  // Open first so the modal is visible while the namespaces load
  showModal.value = true;

  await fetchNamespaces();

  if (!formNamespace.value) {
    formNamespace.value = firstNamespace.value;
  }
}

function openView(row: any) {
  errors.value = [];
  modalMode.value = 'view';
  configModel.value = row;
  formNamespace.value = row.meta?.namespace || '';
  formName.value = row.meta?.name || '';

  const boundapps = row.configuration?.boundapps || [];

  selectedApps.value = [...boundapps];
  initialBoundApps.value = [...boundapps];

  const rows = detailsToRows(row.configuration?.details || {});

  configData.value = [...rows];
  initialConfigDataSnapshot.value = JSON.stringify(rows);

  showModal.value = true;
}

function openEdit(row: any) {
  openView(row);
  modalMode.value = 'edit';
}

function handleModalClose() {
  if (fileDialogActive.value) return;
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  formNamespace.value = '';
  formName.value = '';
  selectedApps.value = [];
  initialBoundApps.value = [];
  configData.value = [];
  initialConfigDataSnapshot.value = '';
  errors.value = [];
  configModel.value = null;
  showDiscardConfirm.value = false;
  showModal.value = false;
}

function addRow() {
  configData.value = [...configData.value, { key: '', value: '' }];
}

function removeRow(i: number) {
  configData.value = configData.value.filter((_, idx) => idx !== i);
}

function updateRowKey(i: number, key: string) {
  configData.value = configData.value.map((row, idx) => idx === i ? { ...row, key } : row);
}

function updateRowValue(i: number, value: string) {
  configData.value = configData.value.map((row, idx) => idx === i ? { ...row, value } : row);
}

// Upload a file and set its contents as the value for row i.
// Uses a single shared input rendered outside the modal so that when the OS
// file dialog closes, focus does not return inside the modal shadow DOM and
// accidentally trigger modal-close.
function triggerValueFileUpload(i: number) {
  if (!rowFileInput.value) return;
  currentUploadRow.value = i;
  fileDialogActive.value = true;
  rowFileInput.value.click();
}

function onRowFileChange(event: Event) {
  fileDialogActive.value = false;
  const file = (event.target as HTMLInputElement).files?.[0];

  if (file && currentUploadRow.value !== null) {
    const row = currentUploadRow.value;
    const reader = new FileReader();

    reader.onload = (e) => {
      updateRowValue(row, (e.target?.result as string) || '');
    };
    reader.readAsText(file);
  }
  (event.target as HTMLInputElement).value = '';
  currentUploadRow.value = null;
}

// "Add from file", parse a KEY=VALUE file and add rows
function triggerBulkFileUpload() {
  fileDialogActive.value = true;
  bulkFileInput.value?.click();
}

// Parse a simple KEY=VALUE file, ignoring empty lines and comments (lines starting with #)
function onBulkFileChange(event: Event) {
  fileDialogActive.value = false;
  const file = (event.target as HTMLInputElement).files?.[0];

  if (!file) return;

  const reader = new FileReader();

  reader.onload = (e) => {
    const text = (e.target?.result as string) || '';
    const newRows: Array<{ key: string; value: string }> = [];

    text.split('\n').forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed || trimmed.startsWith('#')) return;

      const sep = trimmed.indexOf('=');

      if (sep > 0) {
        newRows.push({ key: trimmed.slice(0, sep).trim(), value: trimmed.slice(sep + 1) });
      }
    });

    // If there are new rows, add them to the existing config data. If the existing data is just one empty row, replace it instead.
    if (newRows.length) {
      const existing = configData.value;
      const onlyEmptyRow = existing.length === 1 && !existing[0].key && !existing[0].value;

      configData.value = onlyEmptyRow ? newRows : [...existing, ...newRows];
    }
  };
  reader.readAsText(file);
  (event.target as HTMLInputElement).value = '';
}

// Refresh the whole list, not just the saved record: the table paginates
// server-side, so a single-resource fetch adds an 11th row to a 10-row page
// and leaves the page count stale until the 30s poller catches up.
const refreshConfigurations = () => store
  .dispatch('epinio/refreshList', { type: EPINIO_TYPES.CONFIGURATION })
  .catch(() => {});

// The Bound Applications column reads from the apps slice, so app bindings
// need that list refreshed too.
const refreshApps = () => store
  .dispatch('epinio/findAll', { type: EPINIO_TYPES.APP, opt: { force: true } })
  .catch(() => {});

async function onSubmit() {
  if (!validationPassed.value || saving.value) return;

  saving.value = true;
  errors.value = [];

  try {
    if (isCreate.value) {
      const capturedNamespace = formNamespace.value;
      const capturedName = formName.value;
      const capturedSelectedApps = [...selectedApps.value];

      const cfg = await store.dispatch('epinio/create', { type: EPINIO_TYPES.CONFIGURATION });

      cfg.metadata = { namespace: capturedNamespace, name: capturedName };
      cfg.data = rowsToDetails(configData.value);
      await cfg.create();

      closeModal();

      store.dispatch('growl/success', {
        title:   t('epinio.growl.configuration.create.success.title'),
        message: t('epinio.growl.configuration.create.success.message', { name: capturedName }),
      });

      refreshConfigurations();

      if (capturedSelectedApps.length) {
        const nsApps = store.getters['epinio/all'](EPINIO_TYPES.APP)
          .filter((a: any) => a.meta.namespace === capturedNamespace);

        Promise.all(
          nsApps
            .filter((a: any) => capturedSelectedApps.includes(a.metadata.name))
            .map((a: any) => a.bindConfigurations([capturedName]))
        ).then(() => {
          refreshApps();
          refreshConfigurations();
        }).catch(() => {});
      }
    } else {
      const cfg = configModel.value;
      const capturedNamespace = formNamespace.value;
      const capturedName = cfg.meta?.name;
      const capturedSelectedApps = [...selectedApps.value];
      const capturedInitialApps = [...initialBoundApps.value];
      const dataChanged = JSON.stringify(configData.value) !== initialConfigDataSnapshot.value;

      if (dataChanged) {
        cfg.data = rowsToDetails(configData.value);
        await cfg.update();
      }

      closeModal();

      store.dispatch('growl/success', {
        title:   t('epinio.growl.configuration.update.success.title'),
        message: t('epinio.growl.configuration.update.success.message', { name: capturedName }),
      });

      // Determine which apps were newly bound or unbound, and update accordingly
      const newBindApps = capturedSelectedApps.filter(a => !capturedInitialApps.includes(a));
      const unbindApps = capturedInitialApps.filter(a => !capturedSelectedApps.includes(a));

      if (newBindApps.length || unbindApps.length) {
        const nsApps = store.getters['epinio/all'](EPINIO_TYPES.APP)
          .filter((a: any) => a.meta.namespace === capturedNamespace);

        const bindingOps = nsApps.reduce((ops: Promise<any>[], app: any) => {
          const appName = app.metadata.name;

          if (newBindApps.includes(appName) && !app.configuration?.configurations?.includes(capturedName)) {
            ops.push(app.bindConfigurations([capturedName]));
          } else if (unbindApps.includes(appName)) {
            ops.push(app.unbindConfiguration([capturedName]));
          }

          return ops;
        }, []);

        Promise.all(bindingOps).then(() => {
          refreshApps();
          refreshConfigurations();
        }).catch(() => {});
      }

      cfg.forceFetch().catch(() => {});
    }
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
    store.dispatch('growl/error', {
      title: isEdit.value
        ? t('epinio.growl.configuration.save.error.updateTitle')
        : t('epinio.growl.configuration.save.error.createTitle'),
      message: t('epinio.growl.configuration.save.error.message'),
    });
  } finally {
    saving.value = false;
  }
}

async function fetchApplications() {
  if (!formNamespace.value) return;

  if (cachedApplications.value.length > 0) {
    fetchedApplications.value = cachedApplications.value;
    return;
  }

  isLoadingApplications.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/applications?namespaces=${formNamespace.value}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.APP, ...item })
    ));
    fetchedApplications.value = classifiedData;
    cachedApplications.value = classifiedData;
  } catch (error) {
    console.error('Failed to fetch applications', error);
  } finally {
    isLoadingApplications.value = false;
  }
}  

async function searchApplications(query: string) {
  if (!formNamespace.value) return;

  isLoadingApplications.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/applications?namespaces=${formNamespace.value}&search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.APP, ...item })
    ));
    fetchedApplications.value = classifiedData;
  } catch {
    fetchedApplications.value = [];
  } finally {
    isLoadingApplications.value = false;
  }
}

// watch namespace changes to fetch applications for the selected namespace
watch(formNamespace, (newNamespace) => {
  if (newNamespace) {
    fetchedApplications.value = [];
    cachedApplications.value = [];
    fetchApplications();
  }
}, { immediate: true });

__expose({ openCreate, openView, openEdit });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("input", {
      ref_key: "bulkFileInput",
      ref: bulkFileInput,
      type: "file",
      class: "hidden-file-input",
      onChange: onBulkFileChange,
      onCancel: _cache[0] || (_cache[0] = ($event: any) => (fileDialogActive.value = false))
    }, null, 544),
    _cache[25] || (_cache[25] = _createTextVNode()),
    _createElementVNode("input", {
      ref_key: "rowFileInput",
      ref: rowFileInput,
      type: "file",
      class: "hidden-file-input",
      onChange: onRowFileChange,
      onCancel: _cache[1] || (_cache[1] = ($event: any) => (fileDialogActive.value = false))
    }, null, 544),
    _cache[26] || (_cache[26] = _createTextVNode()),
    _createElementVNode("trailhand-modal", {
      ".open": showModal.value,
      ".dismissible": false,
      title: modalTitle.value,
      subtitle: modalSubtitle.value,
      onModalClose: handleModalClose
    }, [
      _createElementVNode("div", _hoisted_2, [
        (!isCreate.value && configModel.value?.configuration?.origin)
          ? (_openBlock(), _createBlock(Banner, {
              key: 0,
              color: "info",
              label: `This configuration is managed by the '${configModel.value.configuration.origin}' service. It cannot be edited or deleted here. To make changes, update the '${configModel.value.configuration.origin}' service.`
            }, null, 8, ["label"]))
          : _createCommentVNode("", true),
        _cache[17] || (_cache[17] = _createTextVNode()),
        _createElementVNode("trailhand-form-card", null, [
          _createElementVNode("trailhand-form-row", _hoisted_3, [
            (isCreate.value)
              ? (_openBlock(), _createBlock(ResourceDropdown, {
                  key: 0,
                  options: _unref(namespaceOpts),
                  value: formNamespace.value,
                  label: "Namespace",
                  required: "",
                  placeholder: "Select a namespace",
                  onDropdownChange: (e) => { formNamespace.value = e.detail.value; selectedApps.value = []; },
                  fetchAllResources: _unref(fetchNamespaces),
                  searchResources: _unref(searchNamespaces),
                  isLoading: _unref(isLoadingNamespaces)
                }, null, 8, ["options", "value", "onDropdownChange", "fetchAllResources", "searchResources", "isLoading"]))
              : (_openBlock(), _createElementBlock("trailhand-text-input", {
                  key: 1,
                  value: formNamespace.value,
                  label: "Namespace",
                  disabled: true
                }, null, 8, _hoisted_4)),
            _cache[4] || (_cache[4] = _createTextVNode()),
            _createElementVNode("trailhand-text-input", {
              value: formName.value,
              label: "Name",
              required: isCreate.value,
              disabled: !isCreate.value,
              placeholder: "A unique name",
              onTextInputChange: _cache[2] || (_cache[2] = (e) => { formName.value = e.detail.value; })
            }, null, 40, _hoisted_5)
          ]),
          _cache[15] || (_cache[15] = _createTextVNode()),
          _createElementVNode("trailhand-form-row", null, [
            _createVNode(ResourceDropdown, {
              values: selectedApps.value,
              options: nsAppOptions.value,
              label: "Bind to Application (Optional)",
              disabled: isView.value,
              filterable: "",
              multiselect: "",
              placeholder: "Select applications to bind",
              onDropdownChange: (e) => { selectedApps.value = e.detail.values; },
              fetchAllResources: fetchApplications,
              searchResources: searchApplications,
              isLoading: isLoadingApplications.value
            }, null, 8, ["values", "options", "disabled", "onDropdownChange", "isLoading"])
          ]),
          _cache[16] || (_cache[16] = _createTextVNode()),
          _createElementVNode("div", _hoisted_6, [
            _cache[13] || (_cache[13] = _createElementVNode("div", { class: "config-data-section-title" }, "\n            Config Data\n          ", -1)),
            _cache[14] || (_cache[14] = _createTextVNode()),
            _createElementVNode("div", _hoisted_7, [
              (configData.value.length === 0 && !isEditing.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, "\n              No configuration data.\n            "))
                : _createCommentVNode("", true),
              _cache[11] || (_cache[11] = _createTextVNode()),
              (configData.value.length > 0 || isEditing.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "config-data-header" }, [
                      _createElementVNode("span", null, [
                        _createTextVNode("Name "),
                        _createElementVNode("span", { class: "required" }, "*")
                      ]),
                      _createTextVNode(),
                      _createElementVNode("span", null, [
                        _createTextVNode("Value "),
                        _createElementVNode("span", { class: "required" }, "*")
                      ]),
                      _createTextVNode(),
                      _createElementVNode("span"),
                      _createTextVNode(),
                      _createElementVNode("span")
                    ], -1)),
                    _cache[9] || (_cache[9] = _createTextVNode()),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(configData.value, (row, i) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: i,
                        class: "config-data-row"
                      }, [
                        _createElementVNode("trailhand-text-input", {
                          value: row.key,
                          placeholder: "e.g. foo",
                          disabled: isView.value,
                          onTextInputChange: (e) => updateRowKey(i, e.detail.value)
                        }, null, 40, _hoisted_9),
                        _cache[5] || (_cache[5] = _createTextVNode()),
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("trailhand-code-editor", {
                            value: row.value,
                            disabled: isView.value,
                            onCodeInputChange: (e) => updateRowValue(i, e.detail.value)
                          }, null, 40, _hoisted_11)
                        ]),
                        _cache[6] || (_cache[6] = _createTextVNode()),
                        (isEditing.value)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              class: "upload-link",
                              onClick: ($event: any) => (triggerValueFileUpload(i))
                            }, "\n                  Upload\n                ", 8, _hoisted_12))
                          : _createCommentVNode("", true),
                        _cache[7] || (_cache[7] = _createTextVNode()),
                        (isEditing.value)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 1,
                              class: "remove-link",
                              onClick: ($event: any) => (removeRow(i))
                            }, "\n                  Remove\n                ", 8, _hoisted_13))
                          : _createCommentVNode("", true)
                      ]))
                    }), 128))
                  ], 64))
                : _createCommentVNode("", true),
              _cache[12] || (_cache[12] = _createTextVNode()),
              (isEditing.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _createElementVNode("trailhand-button", {
                      variant: "secondary",
                      size: "small",
                      onButtonClick: addRow
                    }, "\n                Add\n              ", 32),
                    _cache[10] || (_cache[10] = _createTextVNode()),
                    _createElementVNode("trailhand-button", {
                      variant: "secondary",
                      size: "small",
                      onButtonClick: triggerBulkFileUpload
                    }, "\n                Read From File\n              ", 32)
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _cache[18] || (_cache[18] = _createTextVNode()),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
          return (_openBlock(), _createBlock(Banner, {
            key: i,
            color: "error",
            label: err
          }, null, 8, ["label"]))
        }), 128))
      ]),
      _cache[24] || (_cache[24] = _createTextVNode()),
      _createElementVNode("div", _hoisted_15, [
        (isView.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: closeModal
              }, "\n          Close\n        ", 32),
              _cache[19] || (_cache[19] = _createTextVNode()),
              (configModel.value?.configuration?.type === 'custom')
                ? (_openBlock(), _createElementBlock("trailhand-button", {
                    key: 0,
                    variant: "primary",
                    onButtonClick: _cache[3] || (_cache[3] = ($event: any) => (modalMode.value = 'edit'))
                  }, "\n          Edit Configuration\n        ", 32))
                : _createCommentVNode("", true)
            ], 64))
          : (showDiscardConfirm.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _cache[20] || (_cache[20] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
                _cache[21] || (_cache[21] = _createTextVNode()),
                _createElementVNode("trailhand-button", {
                  variant: "secondary",
                  class: "mr-10",
                  onButtonClick: handleKeepEditing
                }, "\n          Keep Editing\n        ", 32),
                _cache[22] || (_cache[22] = _createTextVNode()),
                _createElementVNode("trailhand-button", {
                  variant: "destructive",
                  onButtonClick: handleDiscard
                }, "\n          Discard\n        ", 32)
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                _createElementVNode("trailhand-button", {
                  variant: "secondary",
                  class: "mr-10",
                  onButtonClick: handleModalClose
                }, "\n          Cancel\n        ", 32),
                _cache[23] || (_cache[23] = _createTextVNode()),
                _createElementVNode("trailhand-button", {
                  variant: "primary",
                  disabled: !validationPassed.value || saving.value,
                  onButtonClick: onSubmit
                }, _toDisplayString(saving.value ? (isCreate.value ? 'Creating...' : 'Saving...') : (isCreate.value ? 'Create' : 'Save')), 41, _hoisted_16)
              ], 64))
      ])
    ], 40, _hoisted_1)
  ], 64))
}
}

})